import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductVisibilityType } from "@omni/types/api/product/bff/shared/enums/productVisibilityType.enum";
import { StoreType } from "@omni/types/api/store/cloud/shared/enums/storeType.enum";
import { CloudStore } from "@omni/types/api/store/cloud/store.model";
import { ECommerceTooltips } from "@omni/types/components/product/eCommerce/eCommerce.type";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useEffect } from "react";

import { TProductDetailState } from "@/redux/modules/value/productDetail/productDetail.types";
import { GetStoreStrategy } from "@/services/store.service";
import { AdditionalServiceModel } from "@/types/service/shared";
import { Config } from "@/utils/config.utils";
import { getPossibleDeliveryOptions } from "@/utils/deliveryMethod";
import { StorePickerModalStates } from "@/utils/enums";
import { useAssociatedStore } from "@/utils/hooks/store/useAssociatedStore.hook";
import { useStore } from "@/utils/hooks/store/useStore.hook";
import { sanitizeQuantity } from "@/utils/productQuantity.utils";

import { DeliveryOption } from "../../../eCommerce.types";

type DeliveryContextValue = {
    sku: string;
    options: Array<DeliveryOption>;
    activeDelivery: DeliveryType;
    activeQuantity: number;
    changeDelivery: (deliveryType: DeliveryType) => void;
    changeServiceProduct: (
        serviceProduct: AdditionalServiceModel,
        deliveryType: DeliveryType
    ) => void;
    changeQuantity: (amount: number) => void;
    activeServiceProducts?: Array<string>;
    roundingValue: number;
    xfPath?: string | null;
    preferredStore: CloudStore | null;
    minimumOrderQuantity: number;
    tooltips: ECommerceTooltips;
    isLimitedStock: boolean;
    infoLink: string;
    deliveryTypesDisabled: boolean;
    isBoxArticle: boolean;
    deliveryTypes: string[];
    montageParents: string[];
    montages: Array<AdditionalServiceModel>;
    boxLogisticInformation?: string | null;
    isBoxSoldOut: boolean;
    visibilityType: ProductVisibilityType;
    isUpdatingCart: boolean;
    setStorePickerModalState: React.Dispatch<
        React.SetStateAction<StorePickerModalStates>
    >;
};

export type DeliveryContextProviderProps = {
    sku: string;
    visibilityType: ProductVisibilityType;
    deliveryType: DeliveryType;
    quantity: number;
    serviceProductSkus?: string[];
    deliveryOptions: Array<DeliveryOption>;
    onProductDetailChange: (data: Partial<TProductDetailState>) => Promise<{
        type: string;
        payload: Partial<TProductDetailState>;
    }>;
    minimumOrderQuantity: number;
    roundingValue: number;
    xfPath?: string | null;
    tooltips: ECommerceTooltips;
    isLimitedStock: boolean;
    infoLink: string;
    deliveryTypesDisabled: boolean;
    isBoxArticle: boolean;
    deliveryTypes: string[];
    montageParents: string[];
    isUpdatingCart: boolean;
    montages: Array<AdditionalServiceModel>;
    boxLogisticInformation?: string | null;
    isBoxSoldOut: boolean;
    setStorePickerModalState: React.Dispatch<
        React.SetStateAction<StorePickerModalStates>
    >;
};

export const DeliveryContext = createContext<DeliveryContextValue | undefined>(
    undefined
);

DeliveryContext.displayName = "DeliveryContext";

export const DeliveryContextProvider = ({
    sku,
    visibilityType,
    deliveryType,
    quantity,
    serviceProductSkus,
    deliveryOptions,
    minimumOrderQuantity,
    roundingValue,
    xfPath,
    tooltips,
    isLimitedStock,
    infoLink,
    deliveryTypesDisabled,
    isBoxArticle,
    montageParents,
    montages,
    boxLogisticInformation,
    deliveryTypes,
    isUpdatingCart,
    isBoxSoldOut,
    setStorePickerModalState,
    onProductDetailChange,
    children
}: RequireChildrenFor<DeliveryContextProviderProps>) => {
    const { store } = useStore({
        strategy: GetStoreStrategy.Session,
        sku: sku
    });

    const { associatedStore } = useAssociatedStore({
        sku,
        strategy: GetStoreStrategy.DriveIn,
        type: StoreType.WalkIn
    });

    const onQuantityChange = (amount: number) => {
        onProductDetailChange({ quantity: amount });
    };

    const changeDelivery = (newDeliveryType: DeliveryType) => {
        if (newDeliveryType !== DeliveryType.ClickAndCollect) {
            // For home delivery, you cannot order a product in an arbitrary amount
            const sanitizedQuantity = sanitizeQuantity(
                minimumOrderQuantity,
                Config.general["maximum-order-quantity"],
                roundingValue
            )(quantity ? quantity : minimumOrderQuantity);

            onQuantityChange(sanitizedQuantity);
        } else {
            onQuantityChange(quantity ? quantity : 1);
        }

        if (deliveryType !== newDeliveryType) {
            onProductDetailChange({ deliveryType: newDeliveryType });
        }
    };

    const changeServiceProduct = (
        { sku }: AdditionalServiceModel,
        deliveryType: DeliveryType
    ) => {
        onProductDetailChange({
            deliveryType: deliveryType,
            serviceProductSkus:
                serviceProductSkus?.includes(sku) &&
                activeDelivery === deliveryType
                    ? serviceProductSkus?.filter((s) => s !== sku)
                    : [...(serviceProductSkus || []), sku]
        });
    };

    let activeDelivery = deliveryType;

    const possibleDeliveryOptions = getPossibleDeliveryOptions(
        visibilityType,
        deliveryOptions.map((x) => x.deliveryType)
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        const oneDeliveryOptionPossible =
            possibleDeliveryOptions.length === 1 &&
            activeDelivery !== possibleDeliveryOptions[0];
        if (oneDeliveryOptionPossible) {
            activeDelivery = possibleDeliveryOptions[0];
            changeDelivery(activeDelivery);
        }
    }, [possibleDeliveryOptions]);

    const options = deliveryOptions.filter((deliveryOption) =>
        possibleDeliveryOptions.includes(deliveryOption.deliveryType)
    );
    return (
        <DeliveryContext
            value={{
                sku: sku,
                options,
                changeDelivery,
                changeServiceProduct,
                activeDelivery,
                activeQuantity: quantity,
                activeServiceProducts: serviceProductSkus,
                roundingValue,
                xfPath,
                preferredStore: store || (associatedStore ?? null),
                minimumOrderQuantity,
                tooltips,
                isLimitedStock,
                infoLink,
                deliveryTypesDisabled,
                isBoxArticle,
                montageParents,
                montages,
                boxLogisticInformation,
                deliveryTypes,
                visibilityType,
                isBoxSoldOut,
                setStorePickerModalState,
                changeQuantity: onQuantityChange,
                isUpdatingCart
            }}
        >
            {children}
        </DeliveryContext>
    );
};

export const useDeliveryContext = (): DeliveryContextValue => {
    const context = use<DeliveryContextValue | undefined>(DeliveryContext);
    if (!isDefined(context)) {
        throw new Error(`${DeliveryContext.displayName} is undefined.`);
    }
    return context;
};
